/*
* Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
* This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
* The receipt or possession of this program does not convey any rights to use, reproduce or disclose
* its contents in whole or in part, without the specific written consent of Inovalon, Inc.
* Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
* is a violation of the copyright laws and may subject you to criminal prosecution.
*/
import React, { Fragment } from 'react'
import { ICoverageDiscoveryResponseStateManager, IProcedureCode } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import './PreventativeSection.scss'
import moment from 'moment'
import NumberFormat from 'react-number-format'
import { observer } from 'mobx-react'
import { Collapse, CardBody, Card, CardHeader } from 'reactstrap'

import { CommonUtils } from '../../../../utils/CommonUtils'

export interface IPreventativeSectionProps {
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}
interface IPreventativeSectionState {
	IsPageLoading: boolean
	collapse: number
}
@observer
export class PreventativeSection extends React.Component<IPreventativeSectionProps, IPreventativeSectionState> {
	toggle(e): void {
		const event = e.target.dataset.event
		this.setState({ collapse: this.state.collapse === Number(event) ? 0 : Number(event) })
	}
	constructor(props: Readonly<IPreventativeSectionProps>) {
		super(props)
		this.state = {
			IsPageLoading: true,
			collapse: 0,
		}
		this.toggle = this.toggle.bind(this)
	}

	componentDidMount(): void {
		this.setIsPageLoadState(false)
	}
	setIsPageLoadState(val: boolean) {
		this.setState({
			IsPageLoading: val,
		})
	}

	RenderSectionHeader(y: IProcedureCode) {
		return (
			<tbody className="summaryBody">
				<tr className="summarySection">
					<td colSpan={4}>
						<h4>
							{y.definition} - {y.code}
						</h4>
					</td>
				</tr>
			</tbody>
		)
	}
	RenderEligibleProfessionalAndEligibleTechnical(y: IProcedureCode) {
		if (CommonUtils.isValidDate(y.eligibleProfessional)) {
			return (
				<tbody className="summaryBody">
					<tr>
						<td>Eligible Date Prof:</td>
						<td>{CommonUtils.toDisplayDate(y.eligibleProfessional)}</td>
						<td>Eligible Date Tech:</td>
						<td>{CommonUtils.toDisplayDate(y.eligibleTechnical)}</td>
					</tr>
				</tbody>
			)
		}
	}

	RenderDeductibleTotalAndCoInsurance(y: IProcedureCode) {
		if (y.deductibleTotal >= 0 || y.coInsurance >= 0) {
			return (
				<tbody className="summaryBody">
					<tr className="bottom-border-td">
						<td>Deductible Remaining:</td>
						<td>
							<NumberFormat value={y.deductibleRemaining} displayType={'text'} fixedDecimalScale={true} thousandSeparator={true} prefix={'$'} renderText={value => <span>{value}</span>} /> of <NumberFormat value={y.deductibleTotal} displayType={'text'} thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} prefix={'$'} renderText={value => <span>{value}</span>} />
						</td>
						<td>Co-Insurance:</td>
						<td>
							<NumberFormat value={y.coInsurance * 100} displayType={'text'} fixedDecimalScale={true} format={v => Number(v) + '.00%'} />
						</td>
					</tr>
				</tbody>
			)
		}
	}
	RenderProviderNPIAndPPVDos(y: IProcedureCode, isHcps: boolean | undefined) {
		if (isHcps) {
			if (y.providerNPI !== '' && y.providerNPI !== null) {
				return (
					<tbody className="summaryBody">
						<tr>
							<td>Billing Provider NPI:</td>
							<td>{y.providerNPI}</td>
							<td></td>
							<td></td>
						</tr>
					</tbody>
				)
			}
		} else {
			if (y.pPVDos && y.pPVDos.length > 0) {
				let cnt = 0
				const pPV = y.pPVDos.sort((a, b) => (a.key > b.key ? -1 : 1))
				return pPV.forEach(ppvValue => {
					if (cnt !== 10) {
						cnt++
						return (
							<tbody className="summaryBody">
								<tr>
									<td>Date of Service</td>
									<td>{ppvValue.key}</td>
									<td>Billing Provider NPI:</td>
									<td>{ppvValue.value}</td>
								</tr>
							</tbody>
						)
					}
				})
			}
		}
	}

	RenderPreventativeSection(isHcps?: boolean, procedureCode?: IProcedureCode[]): any {
		const dateFormat = 'YYYY-MM-DDThh:mm:ss'
		if (typeof procedureCode !== 'undefined') {
			const procedureCodes = procedureCode.filter(x => moment(x.eligibleProfessional).format(dateFormat) !== moment('0001-01-01T00:00:00').format(dateFormat) || x.code === '90670' || x.code === '90732').sort((a, b) => (a.definition > b.definition ? 1 : -1))
			return procedureCodes.map((y, index) => {
				return (
					<Fragment key={index}>
						{this.RenderSectionHeader(y)}
						{this.RenderEligibleProfessionalAndEligibleTechnical(y)}
						{this.RenderDeductibleTotalAndCoInsurance(y)}
						{this.RenderProviderNPIAndPPVDos(y, isHcps)}
					</Fragment>
				)
			})
		}
	}

	render(): React.ReactNode {
		const { collapse } = this.state
		const section = this.props.CoverageDiscoveryResponseStateManager.selectedPreventativeSection
		if (typeof this.props.CoverageDiscoveryResponseStateManager.selectedPreventativeSection !== 'undefined') {
			return (
				<div className="accordion-group service-coverage">
					<div id={section.id} className="section" data-loadorder={section.loadOrder} data-index={section.summary.index}>
						<Card style={{ marginBottom: '1rem' }} className="card" key={section.displayOrder}>
							<CardHeader onClick={this.toggle} data-event={section.displayOrder} className="service-coverage accordion-heading whiteText">
								{section.summary.title}
							</CardHeader>
							<Collapse isOpen={collapse === section.displayOrder}>
								<CardBody className="card-body">
									<div className="cdResponsePage">
										<table className="service-types-table">
											<tbody>
												<tr className="summaryDisplaying">
													<td colSpan={4}>
														Displaying {section.summary.selectedSections.length} of {section.summary.sections.length} sections
													</td>
												</tr>
											</tbody>
											{this.RenderPreventativeSection(this.props.CoverageDiscoveryResponseStateManager.selectedPreventativeSection.summary.isHCPCS, this.props.CoverageDiscoveryResponseStateManager.selectedPreventativeSection.summary.procedureCodes)}
										</table>
									</div>
								</CardBody>
							</Collapse>
						</Card>
					</div>
				</div>
			)
		} else return <div></div>
	}
}
